/**
 * Question Editor Dialog
 * Modal wrapper for the Question Editor
 */

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { QuestionEditor } from './QuestionEditor';
import { useQuestionBankStore } from '../../stores/questionBankStore';
import { useQuestion, useCreateQuestion, useUpdateQuestion, useDuplicateQuestion } from '../../hooks/useQuestionBank';
import type { Question } from '../../utils/api/questionBankApi';

interface QuestionEditorDialogProps {
  firmId: string;
}

export function QuestionEditorDialog({ firmId }: QuestionEditorDialogProps) {
  const {
    isQuestionEditorOpen,
    editingQuestionId,
    closeQuestionEditor,
  } = useQuestionBankStore();

  // Fetch question data if editing
  const { data: question } = useQuestion(
    firmId,
    editingQuestionId || ''
  );

  // Mutations
  const createMutation = useCreateQuestion(firmId);
  const updateMutation = useUpdateQuestion(firmId);
  const duplicateMutation = useDuplicateQuestion(firmId);

  const handleSave = async (questionData: Partial<Question>) => {
    try {
      if (editingQuestionId) {
        // Update existing question in place
        await updateMutation.mutateAsync({
          questionId: editingQuestionId,
          updates: questionData,
        });
        closeQuestionEditor();
      } else {
        // Create new question
        await createMutation.mutateAsync(questionData);
        closeQuestionEditor();
      }
    } catch (error: any) {
      console.error('Failed to save question:', error);
      // Error is already handled by the mutation hook (toast)
    }
  };

  const handleSaveAs = async (questionData: Partial<Question>) => {
    try {
      if (!editingQuestionId) {
        // If creating new, just save normally
        await createMutation.mutateAsync(questionData);
        closeQuestionEditor();
        return;
      }
      
      // Duplicate the question first
      const duplicated = await duplicateMutation.mutateAsync(editingQuestionId);
      
      // Apply the changes to the duplicated question
      if (duplicated) {
        await updateMutation.mutateAsync({
          questionId: duplicated.id,
          updates: questionData,
        });
      }
      
      closeQuestionEditor();
    } catch (error) {
      console.error('Failed to save as copy:', error);
    }
  };

  return (
    <Dialog open={isQuestionEditorOpen} onOpenChange={closeQuestionEditor}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto p-0">
        <VisuallyHidden>
          <DialogTitle>
            {editingQuestionId ? 'Edit Question' : 'Create Question'}
          </DialogTitle>
          <DialogDescription>
            {editingQuestionId 
              ? 'Modify the question details and save your changes' 
              : 'Fill out the form to create a new question for your question bank'}
          </DialogDescription>
        </VisuallyHidden>
        <QuestionEditor
          question={question}
          firmId={firmId}
          onSave={handleSave}
          onSaveAs={handleSaveAs}
          onCancel={closeQuestionEditor}
        />
      </DialogContent>
    </Dialog>
  );
}